// packages/analytics/src/replay-policy.ts
//
// KAN-32 A.10 (R10, decision O8) — the replay decision, in one pure function.
//
// O8 amended P2/P3: replay is no longer full-surface or on-by-default. It
// records only while a NAMED, TIME-BOXED investigation is open, on a subset of
// routes, behind a DB-backed runtime switch. Two controls layer:
//
//   VITE_POSTHOG_REPLAY_ENABLED (build-time, O6) — may this deployment record
//                                                  at all? Captain's gate.
//   an active investigation      (runtime, DB)   — is it recording NOW, where,
//                                                  until when?
//
// `REPLAY_POLICY.intelhub.recordable` is the OUTER BOUND and is NOT narrowed.
// An investigation selects a subset within it. The bound is re-enforced here,
// client-side, independently of the API's own validation — so a bad row (API
// regression, direct DB write, corrupted jsonb) cannot widen what is recorded.
// Defence in depth, not belt-and-braces.

import { REPLAY_POLICY } from './registry'
import { flattenPatterns, sanitizeUrl } from './sanitize'

/** The union of recordable + excluded — what `sanitizeUrl` resolves against. */
const PATTERNS = flattenPatterns(REPLAY_POLICY.intelhub)

/** The outer bound. Membership is required; it is never sufficient on its own. */
const RECORDABLE: ReadonlySet<string> = new Set(REPLAY_POLICY.intelhub.recordable)

/**
 * Patterns whose dynamic segment carries a secret in the URL. Even if such a
 * pattern were ever added to `recordable`, it must not be recorded: the value
 * lands in `$current_url` and in every rrweb navigation event.
 */
const SECRET_SEGMENT = /:(token|code|nonce)\b/i

export interface ReplayInvestigation {
  /** Route PATTERNS (registry form, e.g. `/stories/:id`), not raw paths. */
  readonly routes: readonly string[]
  /**
   * The EARLIEST expiry among live investigations (KAN-594 — under
   * multiplicity the server serves the union of routes bounded by the min
   * expiry). Liveness is still decided server-side; the client uses this value
   * in exactly two deny-or-refresh ways, never to extend recording: the 3b
   * check below stops recording once it passes, and `useReplayRecording`
   * schedules the boundary `/me` refresh from it.
   */
  readonly expiresAt: string
}

export interface ReplayDecisionContext {
  /** Precondition 1 — `VITE_POSTHOG_REPLAY_ENABLED === 'true'` (O6). */
  readonly deploymentEnabled: boolean
  /** Precondition 2 — the active investigation the SERVER returned, or null. */
  readonly investigation: ReplayInvestigation | null
  /** Precondition 3 — the current raw pathname; sanitised to a pattern here. */
  readonly pathname: string
  /** Precondition 4 — has the session response resolved? */
  readonly sessionResolved: boolean
  /** Precondition 5 — is the user authenticated? (O2) */
  readonly authenticated: boolean
  /** Precondition 6 — has the user objected? (O3, Art. 21) */
  readonly optedOut: boolean
}

/**
 * Is this route pattern inside the outer bound?
 *
 * Three independent refusals, all of which must pass: not `/admin`-prefixed,
 * no secret-bearing dynamic segment, and present in the registry's
 * `recordable` list. The first two are redundant with the third TODAY — no
 * `/admin` route and no `:token` route is recordable — and that redundancy is
 * the point: it survives someone widening `recordable` by mistake.
 */
export function isWithinOuterBound(pattern: unknown): boolean {
  if (typeof pattern !== 'string' || pattern.length === 0) return false
  if (pattern === '/admin' || pattern.startsWith('/admin/')) return false
  if (SECRET_SEGMENT.test(pattern)) return false
  if (!RECORDABLE.has(pattern)) return false
  return true
}

/**
 * The six preconditions. DENY IS THE DEFAULT RETURN, not a fallthrough: every
 * failure path returns `false` explicitly, and the single `return true` at the
 * end is reachable only after all six have been affirmatively satisfied.
 *
 * Every boolean is compared with `=== true` / `=== false` rather than tested
 * for truthiness. An `undefined` that a caller forgot to supply must deny, and
 * a `1` or `'no'` arriving from untyped JSON must not read as consent.
 *
 * EXPIRY IS NEVER EVALUATED HERE. The server selects
 * `revoked_at IS NULL AND now() BETWEEN starts_at AND expires_at` and simply
 * does not return an expired investigation; the client clock is attacker- and
 * drift-controlled, so it is not an input to this decision. The PRESENCE of an
 * investigation on the session response is the server's assertion that it is
 * live. Do not add a `Date.now()` comparison here — see O8 §7.0b.
 */
export function shouldRecord(ctx: ReplayDecisionContext): boolean {
  if (!ctx || typeof ctx !== 'object') return false

  // 1 — may this deployment record at all? (O6)
  if (ctx.deploymentEnabled !== true) return false

  // 2 — is a named investigation active? (O8)
  const investigation = ctx.investigation
  if (!investigation || typeof investigation !== 'object') return false
  if (!Array.isArray(investigation.routes) || investigation.routes.length === 0) return false

  // 3 — is the CURRENT route inside that investigation's list, and inside the
  //     registry's outer bound? The comparison is on the sanitised pattern, so
  //     `/stories/some-slug` is judged as `/stories/:id` and no raw slug is
  //     ever matched or transmitted (R22).
  const pattern = sanitizeUrl(ctx.pathname, PATTERNS)
  if (!isWithinOuterBound(pattern)) return false
  const scoped = investigation.routes.filter(isWithinOuterBound)
  if (!scoped.includes(pattern)) return false

  // 3b — DENY-ONLY client clock check.
  //
  // O8 says expiry is decided server-side, and it is: the server simply does
  // not return an expired investigation. But /me is fetched on mount, on
  // explicit refreshes, and (KAN-594) via the boundary refresh scheduled just
  // past expiresAt — so between those, a tab holding a stale response would
  // keep recording past expires_at. A time-box that does not actually stop is
  // not a time-box; this check is the deny-only backstop.
  //
  // This check can only ever DENY. It cannot extend recording by a single
  // millisecond, because it runs after the server has already asserted the
  // investigation is live. A fast client clock stops early; a slow one simply
  // falls back to the server's word. Neither can widen the recorded surface,
  // which is the property O8 was protecting.
  if (investigation.expiresAt) {
    const expiry = Date.parse(investigation.expiresAt)
    if (Number.isFinite(expiry) && Date.now() >= expiry) return false
  }

  // 4 — has the session resolved? A control honoured 300 ms late is not
  //     honoured: preconditions 5 and 6 are unknowable until it has.
  if (ctx.sessionResolved !== true) return false

  // 5 — authenticated? Anonymous visitors are never recorded (O2). Anonymous
  //     EVENTS are still captured; that is a separate switch.
  if (ctx.authenticated !== true) return false

  // 6 — has the user objected? (O3, Art. 21 — A.8 persists this server-side.)
  if (ctx.optedOut !== false) return false

  return true
}
